import { BucketingStrategy } from './../../bucket/bucketing-strategy';
import { walkAndBuildTreeNode } from '../alm-dashboard';
import { AwsUtil } from '../../authentication/aws.service';
import { TreeNode } from 'primeng/api';
import {
    ALMBreakDownData,
    GetALMBreakDownQueryDataResult,
    WebApiEndpoints,
    GetALMBreakDownQueryDataRequest,
    ALMMetricValueRollupType,
    FinancialDate,
} from '@liquitics/transaction';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';


export async function createALMHistoricalData(
    awsUtil: AwsUtil,
    request: GetALMBreakDownQueryDataRequest,
    breakDownRollupType: ALMMetricValueRollupType,
    currency: string,
    almBucketing: BucketingStrategy): Promise<ALMHistoricalData[]> {
    const result: GetALMBreakDownQueryDataResult =
        await awsUtil.webAPICall<GetALMBreakDownQueryDataResult>(WebApiEndpoints.QueryALMMetricData, request);
    const metricTrendRootNode = {} as TreeNode;
    if (result.breakDownData) {
        const breakDownData: ALMBreakDownData[] = result.breakDownData
            .sort((bdd1: ALMBreakDownData, bdd2: ALMBreakDownData) => {
                return (bdd1.opDt < bdd2.opDt) ? -1 : 1;
            })

        walkAndBuildTreeNode(
            [breakDownRollupType, 'COUNT'],
            metricTrendRootNode,
            breakDownData,
            ['bdn', 'bdd'],
            ['bdd', 'bdn'],
            ['opDt'],
            -1,
            currency,
            almBucketing);

        const historicalData: ALMHistoricalData[] = metricTrendRootNode.children
            .map((treeNode: TreeNode) => {
                return {
                    date: treeNode.label,
                    financialDate: FinancialDate.fromString(treeNode.label),
                    dateLabel: treeNode.label.substring(5),
                    amount: treeNode['amount0'],
                    count: treeNode['amount1'],
                } as ALMHistoricalData
            });
        const historicalDataByDate = historicalData.reduce((grouped, data) => {
            grouped[data.date] = data;
            return grouped;
        }, {});
        historicalData
            .forEach((historicalData: ALMHistoricalData) => {
                const day15 = historicalData.financialDate.addDays(-15).getDateString();
                const day30 = historicalData.financialDate.addDays(-30).getDateString();
                if (historicalDataByDate[day15]) {
                    historicalData.rolling15DayDiff = historicalData.amount - historicalDataByDate[day15].amount;
                    historicalData.rolling15DayCountDiff = historicalData.count - historicalDataByDate[day15].count;
                }
                if (historicalDataByDate[day30]) {
                    historicalData.rolling30DayDiff = historicalData.amount - historicalDataByDate[day30].amount;
                    historicalData.rolling30DayCountDiff = historicalData.count - historicalDataByDate[day30].count;
                }
            })
        return historicalData;
    }
    return [];
}
export type ALMHistoricalChartMode = 'HISTORY' | 'ROLLING_15_DAY' | 'ROLLING_30_DAY';

export interface ALMHistoricalData {
    dateLabel: string;
    date: string;
    financialDate: FinancialDate,
    amount: number;
    count: number;
    rolling15DayDiff: number;
    rolling30DayDiff: number;
    rolling15DayCountDiff: number;
    rolling30DayCountDiff: number;
}


export function createALMHistoricalChart(divName: string,
    historicalData: ALMHistoricalData[],
    mode: ALMHistoricalChartMode): am4charts.XYChart {

    let valueField;
    if (mode === 'HISTORY') {
        valueField = 'amount';
    } else if (mode === 'ROLLING_15_DAY') {
        valueField = 'rolling15DayDiff';
    } else if (mode === 'ROLLING_30_DAY') {
        valueField = 'rolling30DayDiff';
    }

    const chart = am4core.create(divName, am4charts.XYChart);
    chart.hiddenState.properties.opacity = 0; // this makes initial fade in effect
    chart.data = historicalData;
    chart.logo.visible = false;

    // const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    // categoryAxis.renderer.grid.template.location = 0;
    // categoryAxis.dataFields.category = 'dateLabel';

    let dateAxis = chart.xAxes.push(new am4charts.DateAxis());


    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

    let series = chart.series.push(new am4charts.StepLineSeries());
    // series.dataFields.categoryX = 'dateLabel';
    series.dataFields.dateX = 'date';
    series.dataFields.valueY = valueField;
    series.tooltipText = `{valueY.${valueField}}`;
    series.strokeWidth = 3;
    series.fillOpacity = 0.1;

    let range = valueAxis.createSeriesRange(series);
    range.value = 100000000000;
    range.endValue = 0;
    range.contents.stroke = chart.colors.getIndex(4);
    range.contents.fill = range.contents.stroke;
    range.contents.strokeOpacity = 0.7;
    range.contents.fillOpacity = 0.1;

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = 'panXY';
    chart.cursor.xAxis = dateAxis;
    chart.cursor.snapToSeries = series;

    // Add distinctive colors for each column using adapter
    // series.columns.template.adapter.add('fill', (fill, target) => {
    //     return chart.colors.getIndex(target.dataItem.index);
    // });
    return chart;
}
